import {
  Flag,
  addActualSource,
  Archived,
  countryName,
  formatWhole,
  getActual,
  InvalidInput,
  NotFound,
  removeActualSource,
  toIso,
  unwriteActual,
  writeActual,
  type ActualGoing,
  type ActualPair,
  type ActualRow,
  type ActualScreen,
  type ActualSource,
  type ActualWay,
  type Mode,
  type SchengenWindow,
} from '@trippath/shared';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import type { TFunction } from 'i18next';

import { useLive } from '../../app/live';
import { dayRange } from '../../lib/countryFacts';
import { useReadOnly } from '../../app/readOnly';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; screen: ActualScreen };

/** The files a phone accepts as a timeline or a track. */
const ACCEPT = '.json,.gpx,.kml,.kmz,.geojson,.csv,.fit,.tcx';

/** The Schengen area's limit, and the days each square of its window stands for. */
const LIMIT = 90;
const SQUARE_DAYS = 6;

/** A day as `1 Mar` or `1. märts`. */
function dayOf(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T00:00:00Z`));
}

/** What a stop's nights came to beside the plan. */
function difference(row: ActualRow, t: TFunction): { said: string; changed: boolean } {
  if (!row.plan) return { said: t('actual.newStop'), changed: true };
  if (!row.actual) return { said: t('actual.notReached'), changed: true };
  const more = row.actual.nights - row.plan.nights;
  if (more > 0) return { said: t('actual.more', { count: more }), changed: true };
  if (more < 0) return { said: t('actual.fewer', { count: -more }), changed: true };
  return row.actual.start === row.plan.start
    ? { said: t('actual.same'), changed: false }
    : { said: t('actual.otherDays'), changed: true };
}

/** How a way's mode is drawn: its colour beside its name. */
function WayMode({ colour, name }: { colour: string; name: string }) {
  return (
    <span className="actual-way">
      <i style={{ borderColor: `var(--way-${colour})` }} aria-hidden="true" />
      {name}
    </span>
  );
}

/** The colour each way is drawn in. */
const MODE_COLOUR: Record<Mode, string> = {
  flight: 'flight',
  train: 'train',
  bus: 'bus',
  ferry: 'ferry',
  car: 'car',
  foot: 'plan',
};
const GOING_COLOUR: Record<ActualGoing, string> = {
  flight: 'flight',
  train: 'train',
  road: 'bus',
  foot: 'plan',
};

/** Whether a way goes on the ground: speed tells no train from a bus, or a ferry. */
const onGround = (mode: string) => mode !== 'flight' && mode !== 'foot';

/** How a way went beside how it was planned. */
function WayRow({ way, currency }: { way: ActualWay; currency: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const money = (minor: number) => formatWhole({ minor, currency }, lang);
  const { plan, going } = way;
  const agrees =
    plan !== null && going !== null && (plan === going || (onGround(plan) && onGround(going)));
  const planned = plan && (
    <WayMode
      colour={MODE_COLOUR[plan]}
      name={
        way.plannedTo
          ? t('actual.plannedTo', { mode: t(`mode.${plan}`), city: way.plannedTo })
          : t(`mode.${plan}`)
      }
    />
  );
  const went =
    going &&
    (agrees ? (
      <WayMode colour={MODE_COLOUR[plan]} name={t(`mode.${plan}`)} />
    ) : (
      <WayMode colour={GOING_COLOUR[going]} name={t(`actual.going.${going}`)} />
    ));
  const estimate =
    way.estimate &&
    (way.estimate.min === way.estimate.max
      ? money(way.estimate.min)
      : `${money(way.estimate.min)}–${money(way.estimate.max)}`);
  return (
    <tr className={plan === null || (going !== null && !agrees) ? 'actual-changed' : undefined}>
      <td>
        {way.from} → {way.to}
      </td>
      <td>{planned ?? '—'}</td>
      <td>{went ?? <span className="quiet">{t('actual.unknown')}</span>}</td>
      <td className="actual-n">{Math.round(way.km).toLocaleString(lang)}</td>
      <td className="actual-n">
        {estimate ?? '—'} / {way.paid === null ? '—' : money(way.paid)}
      </td>
    </tr>
  );
}

/** One member's rolling window: its squares, the days used and planned, and what frees up. */
function Window({ window, today }: { window: SchengenWindow; today: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const opens = new Date(`${window.until}T00:00:00Z`).getTime() - 179 * 86_400_000;
  const squares = Array.from({ length: 180 / SQUARE_DAYS }, (_, at) => {
    const days = window.days.filter((day) => {
      const square = Math.floor(
        (new Date(`${day}T00:00:00Z`).getTime() - opens) / 86_400_000 / SQUARE_DAYS,
      );
      return square === at;
    });
    if (days.some((day) => day > today)) return 'ahead';
    return days.length > 0 ? 'in' : '';
  });
  const total = window.used + window.planned;
  return (
    <div className="actual-window-of">
      <span className="small">
        {t('actual.schengenWho', {
          name: window.name,
          country: countryName(window.passport, lang),
          day: dayOf(window.until, lang),
        })}
      </span>
      <div className="actual-window" role="img" aria-label={t('actual.schengenSquares')}>
        {squares.map((kind, at) => (
          // The squares never move: each is its place in the window.
          <i key={at} className={kind} />
        ))}
      </div>
      <div className="actual-country">
        <div className="actual-country-top">
          <b>{t('actual.schengenUsed', { used: window.used, planned: window.planned })}</b>
          <span className="mono">
            {total} / {LIMIT}
          </span>
        </div>
        <div className="actual-bar">
          <span
            className={total > LIMIT ? 'over' : 'warn'}
            style={{ width: `${String(Math.min(100, Math.round((total / LIMIT) * 100)))}%` }}
          />
        </div>
        <small>
          {total > LIMIT
            ? t('actual.schengenOver', { over: total - LIMIT })
            : t('actual.schengenLeft', { left: LIMIT - total })}
          {window.frees &&
            t('actual.schengenFrees', {
              day: dayOf(window.frees.day, lang),
              count: window.frees.days,
            })}
        </small>
      </div>
    </div>
  );
}

/** A file added, as a chip: its kind, its name and days, and how many points it gave. */
function SourceChip({
  source,
  onRemove,
}: {
  source: ActualSource;
  /** Takes it off; none on a trip read only. */
  onRemove?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const kind = source.kind === 'timeline' ? 'JSON' : source.kind.toUpperCase();
  return (
    <li className="actual-source">
      <span className="actual-ext">{kind}</span>
      <span>
        {t('actual.sourceLine', {
          name: source.name,
          range: dayRange(source.firstAt.slice(0, 10), source.lastAt.slice(0, 10), lang),
        })}
      </span>
      <span className="tag tag-ok">
        {t('actual.points', { count: source.count, n: source.count.toLocaleString(lang) })}
      </span>
      {onRemove && (
        <button
          type="button"
          className="icon-button"
          aria-label={t('actual.remove', { name: source.name })}
          onClick={onRemove}
        >
          ×
        </button>
      )}
    </li>
  );
}

/**
 * The plan beside what happened (the `PlanActual` mockup): the files added after a trip, its
 * nights, stops and ways as they were, what happened written into the trip or taken back, the
 * days in each country beside what the passport allows, Schengen's rolling window for each member
 * it counts for, and the year in each country.
 */
export function PlanActual() {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [today] = useState(() => toIso(new Date()));
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [again, setAgain] = useState(0);
  const [notes, setNotes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const files = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let live = true;
    getActual(id, today).then(
      (screen) => {
        if (live) setState({ kind: 'ready', screen });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, today, changed, again]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { screen } = state;
  const reload = () => {
    setAgain((n) => n + 1);
  };
  const failed = (error: unknown) =>
    error instanceof Archived ? t('actual.archived') : t('actual.failed');

  const add = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files as FileList)];
    event.target.value = '';
    setBusy(true);
    const said: string[] = [];
    for (const file of chosen) {
      try {
        const added = await addActualSource(id, file);
        if ('again' in added)
          said.push(t('actual.again', { name: file.name, kept: added.again }));
      } catch (error) {
        const outside =
          error instanceof InvalidInput && error.fields.some((f) => f.problem === 'range');
        said.push(
          error instanceof InvalidInput
            ? t(outside ? 'actual.outside' : 'actual.unread', { name: file.name })
            : failed(error),
        );
      }
    }
    setNotes(said);
    setBusy(false);
    reload();
  };
  const act = (doing: () => Promise<unknown>, done: string) => {
    setBusy(true);
    doing().then(
      () => {
        setNotes(done ? [done] : []);
        setBusy(false);
        reload();
      },
      (error: unknown) => {
        setNotes([error instanceof NotFound ? t('actual.notBack') : failed(error)]);
        setBusy(false);
      },
    );
  };

  const { summary } = screen;
  const added = screen.rows.filter((row) => !row.plan).map((row) => row.city);
  const pair = ({ plan, actual }: ActualPair) => `${String(plan)} / ${String(actual)}`;
  return (
    <div className="page actual-page">
      <header className="page-head">
        <h1>{t('actual.title')}</h1>
        <p className="lead">{t('actual.lead')}</p>
      </header>
      <div className="actual-layout">
        <div className="actual-main">
          <section className="panel" aria-labelledby="actual-sources">
            <div className="panel-head">
              <h2 id="actual-sources" className="panel-title">
                {t('actual.sources')}
              </h2>
              {!locked && (
                <button
                  type="button"
                  className="button button-small"
                  disabled={busy}
                  onClick={() => files.current?.click()}
                >
                  {busy ? t('actual.working') : t('actual.add')}
                </button>
              )}
              <input
                ref={files}
                type="file"
                multiple
                hidden
                accept={ACCEPT}
                aria-label={t('actual.add')}
                onChange={(event) => void add(event)}
              />
            </div>
            {screen.sources.length === 0 && <p className="quiet">{t('actual.noSources')}</p>}
            <ul className="actual-sources">
              {screen.sources.map((source) => (
                <SourceChip
                  key={source.id}
                  source={source}
                  onRemove={
                    locked
                      ? undefined
                      : () => {
                          act(() => removeActualSource(id, source.id), '');
                        }
                  }
                />
              ))}
            </ul>
            {notes.map((note) => (
              <p key={note} className="actual-note" role="status">
                {note}
              </p>
            ))}
            <p className="small quiet">{t('actual.once')}</p>
            <div className="actual-sum">
              <div>
                <span>{t('actual.nights')}</span>
                <b>{pair(summary.nights)}</b>
                <span>{t('actual.nightsSub')}</span>
              </div>
              <div>
                <span>{t('actual.stops')}</span>
                <b>{pair(summary.stops)}</b>
                <span>
                  {added.length > 0
                    ? t('actual.stopsAdded', {
                        names: new Intl.ListFormat(lang).format(added),
                      })
                    : t('actual.same')}
                </span>
              </div>
              <div>
                <span>{t('actual.km')}</span>
                <b>{summary.km.actual.toLocaleString(lang)} km</b>
                <span>{t('actual.kmSub', { km: summary.km.plan.toLocaleString(lang) })}</span>
              </div>
              <div>
                <span>{t('actual.flights')}</span>
                <b>{pair(summary.flights)}</b>
                <span>{t('actual.flightsSub')}</span>
              </div>
            </div>
          </section>

          <section className="panel" aria-labelledby="actual-stops">
            <h2 id="actual-stops" className="panel-title">
              {t('actual.stopsTitle')}
            </h2>
            {/* Scrolled sideways on a phone, by the keyboard too. */}
            <div
              className="table-scroll"
              tabIndex={0}
              role="group"
              aria-labelledby="actual-stops"
            >
              <table className="data">
                <thead>
                  <tr>
                    <th>{t('actual.stop')}</th>
                    <th>{t('actual.plan')}</th>
                    <th>{t('actual.actual')}</th>
                    <th className="actual-n">{t('actual.nightsCol')}</th>
                    <th>{t('actual.difference')}</th>
                  </tr>
                </thead>
                <tbody>
                  {screen.rows.map((row, at) => {
                    const diff = difference(row, t);
                    const range = (span: ActualRow['plan']) =>
                      span ? dayRange(span.start, span.end, lang) : '—';
                    return (
                      <tr key={at} className={row.plan ? undefined : 'actual-changed'}>
                        <td>{row.city}</td>
                        <td>{range(row.plan)}</td>
                        <td>
                          {range(row.actual)}
                          {row.actual && !row.seen && (
                            <small className="quiet"> · {t('actual.assumed')}</small>
                          )}
                        </td>
                        <td className="actual-n">
                          {row.plan?.nights ?? 0} / {row.actual?.nights ?? 0}
                        </td>
                        <td className={diff.changed ? 'actual-diff' : 'actual-same'}>
                          {diff.said}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel" aria-labelledby="actual-ways">
            <h2 id="actual-ways" className="panel-title">
              {t('actual.waysTitle')}
            </h2>
            {screen.ways.length === 0 ? (
              <p className="quiet">{t('actual.noWays')}</p>
            ) : (
              <div
                className="table-scroll"
                tabIndex={0}
                role="group"
                aria-labelledby="actual-ways"
              >
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('actual.way')}</th>
                      <th>{t('actual.plan')}</th>
                      <th>{t('actual.actual')}</th>
                      <th className="actual-n">km</th>
                      <th className="actual-n">{t('actual.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {screen.ways.map((way) => (
                      <WayRow
                        key={`${way.from}-${way.to}`}
                        way={way}
                        currency={screen.currency}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* A trip read only: what happened said beside the plan, not written into it. */}
            {!locked && (
              <div className="actual-actions">
                {screen.written ? (
                  <>
                    <span className="quiet">
                      {t('actual.written', { day: dayOf(screen.written.slice(0, 10), lang) })}
                    </span>
                    <button
                      type="button"
                      className="button"
                      disabled={busy}
                      onClick={() => {
                        act(() => unwriteActual(id), t('actual.takenBack'));
                      }}
                    >
                      {t('actual.takeBack')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link className="button" to={`/trips/${id}`}>
                      {t('actual.keep')}
                    </Link>
                    <button
                      type="button"
                      className="button button-primary"
                      disabled={busy}
                      onClick={() => {
                        act(() => writeActual(id), t('actual.wroteIn'));
                      }}
                    >
                      {t('actual.write')}
                    </button>
                  </>
                )}
              </div>
            )}
            {!locked && <p className="small quiet">{t('actual.writeNote')}</p>}
          </section>
        </div>

        <aside className="actual-side">
          <section className="panel" aria-labelledby="actual-countries">
            <h2 id="actual-countries" className="panel-title">
              {t('actual.countriesTitle')}
            </h2>
            {screen.passport === null && (
              <p className="small">
                <Link to={`/trips/${id}/docs`}>{t('actual.noPassport')}</Link>
              </p>
            )}
            <div className="actual-countries">
              {screen.countries.map((country) => {
                const of = country.free ? null : country.rule?.days;
                return (
                  <div key={country.country} className="actual-country">
                    <div className="actual-country-top">
                      <b>
                        <Flag country={country.country} />
                        {countryName(country.country, lang)}
                      </b>
                      <span className="mono">
                        {of
                          ? t('actual.daysOf', { days: country.days, of })
                          : t('actual.days', { count: country.days })}
                      </span>
                    </div>
                    {of && (
                      <div className="actual-bar">
                        <span
                          className={country.days > of ? 'over' : undefined}
                          style={{
                            width: `${String(Math.min(100, Math.round((country.days / of) * 100)))}%`,
                          }}
                        />
                      </div>
                    )}
                    <small>
                      {country.free
                        ? t('actual.freeToMove')
                        : country.rule
                          ? t(`actual.rule.${country.rule.kind}`, { days: country.rule.days })
                          : t('actual.noRule')}
                    </small>
                  </div>
                );
              })}
            </div>
            <p className="small quiet">{t('actual.countriesNote')}</p>
          </section>

          <section className="panel" aria-labelledby="actual-schengen">
            <h2 id="actual-schengen" className="panel-title">
              {t('actual.schengenTitle')}
            </h2>
            {screen.schengen.length === 0 && (
              <p className="quiet">{t('actual.schengenNone')}</p>
            )}
            {screen.schengen.map((window) => (
              <Window key={window.name} window={window} today={today} />
            ))}
          </section>

          <section className="panel" aria-labelledby="actual-year">
            <h2 id="actual-year" className="panel-title">
              {t('actual.yearTitle', { year: screen.year.year })}
            </h2>
            <table className="data actual-year">
              <tbody>
                {screen.year.countries.map((country) => (
                  <tr key={country.country}>
                    <td>
                      {countryName(country.country, lang)}
                      {country.country === screen.year.home && (
                        <small className="quiet"> · {t('actual.home')}</small>
                      )}
                    </td>
                    <td className="actual-n">{t('actual.days', { count: country.days })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="small quiet">{t('actual.yearNote')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
